import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  bootstrapAuth,
  sendPhoneCode,
  signIn,
  signUp,
  startKakaoLogin,
  verifyPhoneCode,
} from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { useCompanyStore } from '../store/companyStore';
import './LoginPage.css';
import { reportError } from '../utils/notify';

type Mode = 'login' | 'signup';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const hasCompany = useCompanyStore((state) => state.hasCompany);
  const setTokens = useAuthStore((state) => state.setTokens);
  const rememberMePref = useAuthStore((state) => state.rememberMe);
  const setRememberMe = useAuthStore((state) => state.setRememberMe);
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error');
  const fromPath =
    (location.state as { from?: { pathname?: string; search?: string } } | null)?.from?.pathname
      ? `${(location.state as { from: { pathname: string; search?: string } }).from.pathname}${
          (location.state as { from: { pathname: string; search?: string } }).from.search || ''
        }`
      : null;

  // 자체 회원가입 UI는 당분간 비활성 — 신규 가입은 카카오만
  const [mode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [rememberMe, setRememberMeLocal] = useState(rememberMePref);
  const [error, setError] = useState(oauthError ? '로그인에 실패했습니다. 다시 시도해 주세요.' : '');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isHydrated) {
      void bootstrapAuth();
    }
  }, [isHydrated]);

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const timer = window.setInterval(() => {
      setCooldownSec((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSec]);

  if (!isHydrated) {
    return (
      <div className="login-page">
        <p style={{ color: '#667085' }}>로그인 상태 확인 중...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    const dest =
      fromPath && fromPath.startsWith('/') && fromPath !== '/'
        ? fromPath
        : hasCompany
          ? '/app'
          : '/companies';
    return <Navigate to={dest} replace />;
  }

  const resetSignupExtras = () => {
    setPasswordConfirm('');
    setBirthDate('');
    setGender('');
    setPhoneNumber('');
    setOtpCode('');
    setPhoneVerificationToken('');
    setPhoneVerified(false);
    setCooldownSec(0);
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      setRememberMe(rememberMe);
      const token = await signIn(username.trim(), password, rememberMe);
      setTokens({
        accessToken: token.accessToken,
        socialSub: username.trim(),
        rememberMe: token.rememberMe ?? rememberMe,
        sessionExpiresAt: token.sessionExpiresAt ?? null,
      });
      const dest =
        fromPath && fromPath.startsWith('/') && fromPath !== '/'
          ? fromPath
          : '/setup-profile';
      navigate(dest, { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '아이디 또는 비밀번호가 올바르지 않습니다.';
      reportError(setError, message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCode = async () => {
    try {
      setSendingCode(true);
      setError('');
      setSuccess('');
      setPhoneVerified(false);
      setPhoneVerificationToken('');
      await sendPhoneCode(phoneNumber.trim());
      setCooldownSec(60);
      setSuccess('인증번호를 발송했습니다.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '인증번호 발송에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerifyingCode(true);
      setError('');
      setSuccess('');
      const result = await verifyPhoneCode(phoneNumber.trim(), otpCode.trim());
      setPhoneVerificationToken(result.verificationToken);
      setPhoneVerified(true);
      setSuccess('휴대폰 인증이 완료되었습니다.');
    } catch (err: unknown) {
      setPhoneVerified(false);
      setPhoneVerificationToken('');
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '인증번호 확인에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSignUp = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== passwordConfirm) {
      reportError(setError, '비밀번호가 일치하지 않습니다.');
      return;
    }
    if (!phoneVerified || !phoneVerificationToken) {
      reportError(setError, '휴대폰 인증을 완료해 주세요.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      await signUp({
        username: username.trim(),
        password,
        email: email.trim(),
        nickname: name.trim(),
        phoneNumber: phoneNumber.trim(),
        phoneVerificationToken,
        birthDate,
        gender: gender || undefined,
      });
      setSuccess('회원가입이 완료되었습니다. 로그인해 주세요.');
      setPassword('');
      resetSignupExtras();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '회원가입에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className={`login-page__panel${mode === 'signup' ? ' login-page__panel--signup' : ''}`}>
        <div className="login-page__brand">
          <Link to="/" className="login-page__logo">
            <em>Line</em>log
          </Link>
          <p className="login-page__subtitle">
            {mode === 'login' ? '아이디 또는 카카오로 로그인하세요' : '새 계정을 만들어 주세요'}
          </p>
        </div>

        {error && <p className="login-page__error">{error}</p>}
        {success && <p className="login-page__success">{success}</p>}

        <form
          className="login-page__form"
          onSubmit={mode === 'login' ? handleLogin : handleSignUp}
        >
          <label>
            아이디
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label>
            비밀번호
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'signup' ? 8 : undefined}
            />
          </label>

          {mode === 'signup' && (
            <>
              <label>
                비밀번호 확인
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </label>
              <label>
                이름
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="실명 입력"
                  required
                  maxLength={50}
                />
              </label>
              <label>
                이메일
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="필수"
                  required
                />
              </label>
              <label>
                생년월일
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  required
                />
              </label>
              <label>
                성별
                <select value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="">선택</option>
                  <option value="MALE">남</option>
                  <option value="FEMALE">여</option>
                  <option value="OTHER">기타</option>
                </select>
              </label>
              <label>
                휴대폰 번호
                <div className="login-page__phone-row">
                  <input
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      setPhoneVerified(false);
                      setPhoneVerificationToken('');
                    }}
                    placeholder="01012345678"
                    required
                    inputMode="tel"
                  />
                  <button
                    type="button"
                    className="login-page__phone-btn"
                    onClick={() => void handleSendCode()}
                    disabled={sendingCode || cooldownSec > 0 || !phoneNumber.trim()}
                  >
                    {cooldownSec > 0
                      ? `${cooldownSec}초`
                      : sendingCode
                        ? '발송 중...'
                        : '인증번호 발급'}
                  </button>
                </div>
              </label>
              <label>
                인증번호
                <div className="login-page__phone-row">
                  <input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="6자리"
                    required
                    inputMode="numeric"
                    maxLength={6}
                    disabled={phoneVerified}
                  />
                  <button
                    type="button"
                    className="login-page__phone-btn"
                    onClick={() => void handleVerifyCode()}
                    disabled={verifyingCode || phoneVerified || !otpCode.trim()}
                  >
                    {phoneVerified ? '완료' : verifyingCode ? '확인 중...' : '확인'}
                  </button>
                </div>
                {phoneVerified && (
                  <span className="login-page__verified-badge">휴대폰 인증 완료</span>
                )}
              </label>
            </>
          )}

          {mode === 'login' && (
            <label className="login-page__remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  setRememberMeLocal(e.target.checked);
                  setRememberMe(e.target.checked);
                }}
              />
              로그인 유지 (체크 시 7일 / 미체크 시 60분)
            </label>
          )}

          <button
            type="submit"
            className="login-page__submit"
            disabled={
              submitting || (mode === 'signup' && (!phoneVerified || !phoneVerificationToken))
            }
          >
            {submitting
              ? mode === 'login'
                ? '로그인 중...'
                : '가입 중...'
              : mode === 'login'
                ? '로그인'
                : '회원가입'}
          </button>
        </form>

        <div className="login-page__divider">
          <span>또는</span>
        </div>

        <button
          type="button"
          className="login-page__kakao-btn"
          onClick={() => {
            setRememberMe(rememberMe);
            startKakaoLogin(rememberMe);
          }}
        >
          <span className="login-page__kakao-icon" aria-hidden="true">
            💬
          </span>
          카카오로 시작하기
        </button>
        <p className="login-page__hint">신규 가입은 카카오 로그인으로 진행됩니다.</p>
        <p className="login-page__contact">
          문의사항은{' '}
          <a href="mailto:ksg04001@daum.net">ksg04001@daum.net</a>
          으로 연락해 주세요.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
